import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';

import {
  ChatText,
  CheckCircle,
  CircleNotch,
  PaperPlaneRight,
  Robot,
  XCircle
} from '@phosphor-icons/react';
/**
 * OperatorAccessManager - Manage which operators a team member can access
 * 
 * Similar to AgentAccessManager but for Execution Agents (Operators)
 */
const OperatorAccessManager = ({ userId, userName, userRole }) => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [operators, setOperators] = useState([]);
  const [accessMap, setAccessMap] = useState({});
  const [originalAccessMap, setOriginalAccessMap] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
      fetchOperatorAccess();
    }
  }, [userId, activeOrganization]);

  const fetchOperatorAccess = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/users/${userId}/operator-access`,
        {},
        activeOrganization.organization_id
      );

      if (!response.ok) {
        throw new Error('Failed to fetch operator access');
      }

      const data = await response.json();
      
      if (data.is_admin) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      setOperators(data.operators || []);
      
      // Build access map
      const map = {};
      data.operators.forEach(op => {
        map[op.operator_id] = op.has_access;
      });
      
      setAccessMap(map);
      setOriginalAccessMap({ ...map });
      setIsAdmin(false);
      
    } catch (err) {
      console.error('Error fetching operator access:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (operatorId) => {
    setAccessMap(prev => ({
      ...prev,
      [operatorId]: !prev[operatorId]
    }));
  };

  const hasChanges = () => {
    return Object.keys(accessMap).some(
      opId => accessMap[opId] !== originalAccessMap[opId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Build updates array
      const updates = Object.keys(accessMap)
        .filter(opId => accessMap[opId] !== originalAccessMap[opId])
        .map(opId => ({
          agent_id: opId,  // API expects agent_id
          has_access: accessMap[opId]
        }));

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/users/${userId}/operator-access`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            updates: updates
          })
        },
        activeOrganization.organization_id
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update access');
      }

      const result = await response.json();
      setSuccess(result.message);
      setOriginalAccessMap({ ...accessMap });
      toast.success('Operator access updated');
      
    } catch (err) {
      console.error('Error updating operator access:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="py-8 text-center">
          <CircleNotch className="w-6 h-6 animate-spin mx-auto text-pink-500" />
          <p className="text-zinc-400 mt-2">Loading operator access...</p>
        </CardContent>
      </Card>
    );
  }

  if (isAdmin) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Robot className="w-5 h-5 text-pink-500" />
            Operator Access
          </CardTitle>
          <CardDescription>
            Manage which operators this team member can interact with
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="bg-green-500/10 border-green-500/20">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <AlertDescription className="text-green-300">
              {userName || 'This user'} is an admin and has access to all operators by default.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Robot className="w-5 h-5 text-pink-500" />
          Operator Access
        </CardTitle>
        <CardDescription>
          Select which operators {userName || 'this member'} can interact with via chat
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert className="bg-red-500/10 border-red-500/20">
            <XCircle className="w-4 h-4 text-red-400" />
            <AlertDescription className="text-red-300">{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="bg-green-500/10 border-green-500/20">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <AlertDescription className="text-green-300">{success}</AlertDescription>
          </Alert>
        )}

        {operators.length === 0 ? (
          <div className="text-center py-8">
            <Robot className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No operators available</p>
            <p className="text-zinc-500 text-sm">Create an operator first to assign access</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {operators.map(operator => (
                <div
                  key={operator.operator_id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    accessMap[operator.operator_id]
                      ? 'bg-pink-500/10 border-pink-500/30'
                      : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={accessMap[operator.operator_id] || false}
                      onCheckedChange={() => handleToggle(operator.operator_id)}
                      className="data-[state=checked]:bg-pink-500 data-[state=checked]:border-pink-500"
                    />
                    <div>
                      <p className="text-white font-medium">{operator.operator_name}</p>
                      {operator.description && (
                        <p className="text-xs text-zinc-500 line-clamp-1">{operator.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {operator.has_telegram && (
                      <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 text-xs">
                        <PaperPlaneRight className="w-3 h-3 mr-1" />
                        Telegram
                      </Badge>
                    )}
                    {operator.has_whatsapp && (
                      <Badge variant="secondary" className="bg-green-500/20 text-green-400 text-xs">
                        <ChatText className="w-3 h-3 mr-1" />
                        WhatsApp
                      </Badge>
                    )}
                    <Badge 
                      variant="secondary" 
                      className={operator.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-zinc-700 text-zinc-400'
                      }
                    >
                      {operator.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <p className="text-sm text-zinc-500">
                {Object.values(accessMap).filter(Boolean).length} of {operators.length} operators accessible
              </p>
              <Button
                onClick={handleSave}
                disabled={!hasChanges() || saving}
                className="bg-pink-500 hover:bg-pink-600"
              >
                {saving ? (
                  <>
                    <CircleNotch className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OperatorAccessManager;
